import { supabase } from '@/integrations/supabase/client';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import {
  createVaultRun,
  fetchVaultPages,
  slugifyVaultSegment,
  updateVaultRun,
  upsertVaultPage,
} from '@/lib/librarian/api';
import { initializeVault } from '@/lib/librarian/initializeVault';
import { syncKnowledgeGraphForIngest } from '@/lib/librarian/graph';
import { synthesizeDocumentForLibrarian, type LibrarianDocumentSynthesis } from '@/lib/librarian/synthesizeDocument';
import { updateIndexPage } from '@/lib/librarian/updateIndexPage';

import { updateLogPage } from '@/lib/librarian/updateLogPage';
import type { KnowledgeBaseVaultPage, LibrarianIngestResult } from '@/lib/librarian/types';

function bulletList(items: string[], emptyLabel: string) {
  return items.length > 0 ? items.map((item) => `- ${item}`) : [`- ${emptyLabel}`];
}

function buildSourcePage(document: KnowledgeBaseDocument, synthesis: LibrarianDocumentSynthesis) {
  const sourcePath = `sources/${slugifyVaultSegment(document.name)}.md`;
  const topicLinks = synthesis.topics.length > 0
    ? synthesis.topics.map((topic) => `- [${topic.name}](../topics/${slugifyVaultSegment(topic.name)}.md) — ${topic.summary}`)
    : ['- None detected yet'];
  const entityLinks = synthesis.entities.length > 0
    ? synthesis.entities.map((entity) => `- [${entity.name}](../entities/${slugifyVaultSegment(entity.name)}.md)${entity.type ? ` (${entity.type})` : ''} — ${entity.summary}`)
    : ['- None detected yet'];

  const content = [
    `# Source: ${document.name}`,
    '',
    `- File type: ${document.file_type}`,
    `- Size: ${document.size} bytes`,
    `- Last synced: ${new Date().toISOString()}`,
    `- AI synthesis: ${synthesis.usedAI ? 'enabled' : 'fallback mode'}`,
    '',
    '## Executive Summary',
    synthesis.summary,
    '',
    '## Key Insights',
    ...bulletList(synthesis.keyInsights, 'No key insights were extracted yet.'),
    '',
    '## Related Topics',
    ...topicLinks,
    '',
    '## Related Entities',
    ...entityLinks,
    '',
    '## Source Highlights',
    ...bulletList(synthesis.sourceHighlights, 'No source highlights were extracted yet.'),
    '',
    '## Open Questions',
    ...bulletList(synthesis.openQuestions, 'No open questions were captured from this source.'),
    '',
    '## Source Excerpt',
    document.content.slice(0, 5000),
  ].join('\n');

  return { path: sourcePath, content };
}

function buildTopicContent(topic: LibrarianDocumentSynthesis['topics'][number], document: KnowledgeBaseDocument, sourcePath: string, sourceIds: string[]) {
  return [
    `# Topic: ${topic.name}`,
    '',
    '## Summary',
    topic.summary,
    '',
    '## Why this matters in the source',
    ...bulletList(topic.evidence, `This topic was extracted from [${document.name}](${sourcePath}).`),
    '',
    '## Related Entities',
    ...(topic.relatedEntities.length > 0
      ? topic.relatedEntities.map((entity) => `- [${entity}](../entities/${slugifyVaultSegment(entity)}.md)`)
      : ['- None linked yet']),
    '',
    '## Supporting Sources',
    ...sourceIds.map((id) => (id === document.id ? `- [${document.name}](${sourcePath})` : `- Source document ${id}`)),
  ].join('\n');
}

function buildEntityContent(entity: LibrarianDocumentSynthesis['entities'][number], document: KnowledgeBaseDocument, sourcePath: string, sourceIds: string[]) {
  return [
    `# Entity: ${entity.name}`,
    '',
    ...(entity.type ? [`- Type: ${entity.type}`, ''] : []),
    '## Summary',
    entity.summary,
    '',
    '## Evidence from Sources',
    ...bulletList(entity.evidence, `Referenced in [${document.name}](${sourcePath}).`),
    '',
    '## Related Topics',
    ...(entity.relatedTopics.length > 0
      ? entity.relatedTopics.map((topic) => `- [${topic}](../topics/${slugifyVaultSegment(topic)}.md)`)
      : ['- None linked yet']),
    '',
    '## Supporting Sources',
    ...sourceIds.map((id) => (id === document.id ? `- [${document.name}](${sourcePath})` : `- Source document ${id}`)),
  ].join('\n');
}

function buildSynthesisPage(document: KnowledgeBaseDocument, synthesis: LibrarianDocumentSynthesis, sourcePath: string) {
  const synthesisPath = `syntheses/${slugifyVaultSegment(document.name)}-synthesis.md`;
  const content = [
    `# Synthesis: ${document.name}`,
    '',
    `Source: [${document.name}](${sourcePath})`,
    '',
    synthesis.synthesisMarkdown.trim(),
  ].join('\n');

  return { path: synthesisPath, content };
}

function buildOverviewPage(
  document: KnowledgeBaseDocument,
  synthesis: LibrarianDocumentSynthesis,
  sourcePath: string,
  synthesisPath: string
) {
  return [
    '# Knowledge Base Overview',
    '',
    `Most recent source: [${document.name}](${sourcePath})`,
    `Latest synthesis: [${document.name}](${synthesisPath})`,
    '',
    '## Current Understanding',
    synthesis.overviewSummary,
    '',
    '## Key Insights from Latest Ingest',
    ...bulletList(synthesis.keyInsights, 'No key insights available yet.'),
    '',
    '## Emerging Topics',
    ...(synthesis.topics.length > 0
      ? synthesis.topics.map((topic) => `- [${topic.name}](topics/${slugifyVaultSegment(topic.name)}.md) — ${topic.summary}`)
      : ['- None detected yet']),
    '',
    '## Notable Entities',
    ...(synthesis.entities.length > 0
      ? synthesis.entities.map((entity) => `- [${entity.name}](entities/${slugifyVaultSegment(entity.name)}.md) — ${entity.summary}`)
      : ['- None detected yet']),
    '',
    '## Open Questions',
    ...bulletList(synthesis.openQuestions, 'No open questions were captured yet.'),
  ].join('\n');
}

export async function ingestSourceDocument(documentId: string, userId?: string | null): Promise<LibrarianIngestResult> {
  const { data, error } = await supabase
    .from('knowledge_base_documents')
    .select('*')
    .eq('id', documentId)
    .single();

  if (error) throw error;

  const document = data as KnowledgeBaseDocument;
  const vault = await initializeVault(document.knowledge_base_id);
  const run = await createVaultRun({
    vaultId: vault.id,
    runType: 'ingest',
    status: 'running',
    inputDocumentIds: [document.id],
    createdBy: userId ?? null,
    notes: `Processing source document "${document.name}" with Librarian AI synthesis.`,
  });

  const existingPages = await fetchVaultPages(vault.id);
  const existingByPath = new Map(existingPages.map((page) => [page.path, page]));
  const createdPages: KnowledgeBaseVaultPage[] = [];
  const updatedPages: KnowledgeBaseVaultPage[] = [];
  const touchedPages: KnowledgeBaseVaultPage[] = [];
  const touch = (page: KnowledgeBaseVaultPage) => {
    touchedPages.push(page);
    if (existingByPath.has(page.path)) {
      updatedPages.push(page);
    } else {
      createdPages.push(page);
    }
    existingByPath.set(page.path, page);
  };

  const synthesis = await synthesizeDocumentForLibrarian(document);
  const issues = [...synthesis.issues];

  const sourcePage = buildSourcePage(document, synthesis);
  touch(
    await upsertVaultPage({
      vaultId: vault.id,
      path: sourcePage.path,
      title: `Source · ${document.name}`,
      pageType: 'source',
      contentMarkdown: sourcePage.content,
      summary: synthesis.summary,
      sourceDocumentIds: [document.id],
      metadata: {
        fileType: document.file_type,
        size: document.size,
        sourceName: document.name,
        aiSynthesized: synthesis.usedAI,
      },
    })
  );

  for (const topic of synthesis.topics) {
    const path = `topics/${slugifyVaultSegment(topic.name)}.md`;
    const previous = existingByPath.get(path);
    const sourceIds = Array.from(new Set([...(previous?.source_document_ids ?? []), document.id]));

    touch(
      await upsertVaultPage({
        vaultId: vault.id,
        path,
        title: topic.name,
        pageType: 'topic',
        contentMarkdown: buildTopicContent(topic, document, sourcePage.path, sourceIds),
        summary: topic.summary,
        sourceDocumentIds: sourceIds,
        metadata: {
          latestSource: document.name,
          relatedEntities: topic.relatedEntities,
          aiSynthesized: synthesis.usedAI,
        },
      })
    );
  }

  for (const entity of synthesis.entities) {
    const path = `entities/${slugifyVaultSegment(entity.name)}.md`;
    const previous = existingByPath.get(path);
    const sourceIds = Array.from(new Set([...(previous?.source_document_ids ?? []), document.id]));

    touch(
      await upsertVaultPage({
        vaultId: vault.id,
        path,
        title: entity.name,
        pageType: 'entity',
        contentMarkdown: buildEntityContent(entity, document, sourcePage.path, sourceIds),
        summary: entity.summary,
        sourceDocumentIds: sourceIds,
        metadata: {
          entityType: entity.type,
          latestSource: document.name,
          relatedTopics: entity.relatedTopics,
          aiSynthesized: synthesis.usedAI,
        },
      })
    );
  }

  const synthesisPage = buildSynthesisPage(document, synthesis, sourcePage.path);
  touch(
    await upsertVaultPage({
      vaultId: vault.id,
      path: synthesisPage.path,
      title: `Synthesis · ${document.name}`,
      pageType: 'synthesis',
      contentMarkdown: synthesisPage.content,
      summary: synthesis.overviewSummary,
      sourceDocumentIds: [document.id],
      metadata: {
        sourceName: document.name,
        aiSynthesized: synthesis.usedAI,
      },
    })
  );

  const overview = await upsertVaultPage({
    vaultId: vault.id,
    path: 'overview.md',
    title: 'Overview',
    pageType: 'overview',
    contentMarkdown: buildOverviewPage(document, synthesis, sourcePage.path, synthesisPage.path),
    summary: synthesis.overviewSummary,
    sourceDocumentIds: [document.id],
    metadata: {
      lastIngestedDocumentId: document.id,
      lastIngestedDocumentName: document.name,
      aiSynthesized: synthesis.usedAI,
    },
  });
  touch(overview);

  await updateIndexPage(vault);
  await updateLogPage(
    vault,
    synthesis.usedAI
      ? `Ingested source "${document.name}" with AI synthesis and refreshed ${touchedPages.length} vault pages.`
      : `Ingested source "${document.name}" with fallback synthesis and refreshed ${touchedPages.length} vault pages.`
  );

  const refreshedPages = await fetchVaultPages(vault.id);
  const indexPage = refreshedPages.find((page) => page.path === 'index.md');
  const logPage = refreshedPages.find((page) => page.path === 'log.md');
  if (indexPage) touch(indexPage);
  if (logPage) touch(logPage);

  const completedRun = await updateVaultRun(run.id, {
    status: 'completed',
    outputPageIds: touchedPages.map((page) => page.id),
    notes: issues.length > 0
      ? issues.join(' ')
      : `Completed AI synthesis for ${document.name} with ${synthesis.topics.length} topics and ${synthesis.entities.length} entities.`,
  });

  await syncKnowledgeGraphForIngest({
    vault,
    run: completedRun,
    document,
    synthesis,
    createdBy: userId ?? null,
  });

  return {
    run: completedRun,
    createdPages,
    updatedPages,
    issues,
  };
}
